import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { calculateHealthScore, formatHealthScore } from '../utils/businessHealthScore'

const GRADE_CRITERIA = [
  { grade: 'A+', range: '85-100' },
  { grade: 'B', range: '70-84' },
  { grade: 'C', range: '50-69' },
  { grade: 'D', range: '0-49' },
]

const STATUS_STYLES = {
  excellent: 'bg-emerald-50 text-emerald-700',
  good: 'bg-emerald-50 text-emerald-700',
  warning: 'bg-amber-50 text-amber-700',
  poor: 'bg-rose-50 text-rose-700',
  neutral: 'bg-gray-100 text-gray-600',
}

const STATUS_TEXT_STYLES = {
  excellent: 'text-emerald-700',
  good: 'text-emerald-700',
  warning: 'text-amber-700',
  poor: 'text-rose-700',
  neutral: 'text-gray-600',
}

const HealthScoreCard = React.memo(({ currentMetrics }) => {
  const [showGradeCriteria, setShowGradeCriteria] = useState(false)
  const [showBreakdown, setShowBreakdown] = useState(false)
  const { t } = useTranslation('dashboard')

  const healthData = useMemo(
    () => (currentMetrics ? calculateHealthScore(currentMetrics) : null),
    [currentMetrics]
  )
  const displayData = useMemo(() => {
    if (!healthData) return null
    return { formattedScore: formatHealthScore(healthData.score) }
  }, [healthData])

  if (!currentMetrics || !healthData || !displayData) {
    return (
      <section className="h-full rounded-[14px] border border-gray-200 bg-white p-5">
        <h2 className="text-[15px] font-bold text-gray-900">{t('healthScore.compactTitle')}</h2>
        <p className="mt-4 text-sm text-gray-500">{t('healthScore.emptyMessage')}</p>
      </section>
    )
  }

  const { score, status, breakdown, recommendations } = healthData
  const { formattedScore } = displayData
  const circumference = 2 * Math.PI * 37
  const strokeDashoffset = circumference - (score / 100) * circumference
  const recommendation = recommendations[0]
  const scoreBadgeClass = STATUS_STYLES[status] || STATUS_STYLES.neutral

  const breakdownNotes = {
    profitMargin: t('healthScore.breakdownNotes.profitMargin'),
    conversionRate: t('healthScore.breakdownNotes.conversionRate'),
    revenueGrowth: t('healthScore.breakdownNotes.revenueGrowth'),
    expenseRatio: t('healthScore.breakdownNotes.expenseRatio'),
    netProfit: t('healthScore.breakdownNotes.netProfit'),
  }

  const breakdownEntries = Object.entries(breakdown)
  const breakdownCounts = breakdownEntries.reduce((counts, [, data]) => {
    if (data.status === 'excellent' || data.status === 'good') counts.strong += 1
    else if (data.status === 'warning' || data.status === 'poor') counts.developing += 1
    else counts.noData += 1
    return counts
  }, { strong: 0, developing: 0, noData: 0 })
  const metricStrengthSummary = [
    breakdownCounts.strong > 0 && t('healthScore.breakdown.strongCount', { count: breakdownCounts.strong }),
    breakdownCounts.developing > 0 && t('healthScore.breakdown.developingCount', { count: breakdownCounts.developing }),
    breakdownCounts.noData > 0 && t('healthScore.breakdown.noDataCount', { count: breakdownCounts.noData }),
  ].filter(Boolean).join(' · ')

  const getProgressColor = (metricStatus) => {
    if (metricStatus === 'excellent' || metricStatus === 'good') return 'bg-emerald-500'
    if (metricStatus === 'warning') return 'bg-amber-500'
    if (metricStatus === 'poor') return 'bg-rose-500'
    return 'bg-gray-400'
  }

  const breakdownContent = (
    <div className="space-y-6">
      <section
        aria-label={t('healthScore.breakdown.overallScore')}
        className="grid grid-cols-1 items-center gap-6 rounded-2xl border border-gray-200 bg-gray-50 p-5 sm:p-6 lg:grid-cols-[minmax(0,1fr)_320px]"
      >
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.12em] text-gray-500">
            {t('healthScore.breakdown.overallScore')}
          </p>
          <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-2">
            <span className="text-5xl font-bold leading-none tracking-tight text-gray-900">{score}</span>
            <span className="text-lg font-medium text-gray-400">/ 100</span>
            <span className={`ml-1 rounded-full px-3 py-1.5 text-xs font-bold ${scoreBadgeClass}`}>
              {formattedScore.description}
            </span>
          </div>
          <p className="mt-2 text-sm leading-5 text-gray-600">{t('healthScore.metricsBasis')}</p>
        </div>

        <div className="min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs">
            <span className="font-semibold text-gray-700">{t('healthScore.breakdown.metricStrength')}</span>
            <span className="text-right font-medium text-gray-600">{metricStrengthSummary}</span>
          </div>
          <div role="img" aria-label={metricStrengthSummary} className="mt-3 flex gap-1.5">
            {breakdownEntries.map(([metric, data]) => (
              <span
                key={metric}
                title={t(`healthScore.metrics.${metric}`)}
                className={`h-2.5 min-w-0 flex-1 rounded-full ${getProgressColor(data.status)}`}
              />
            ))}
          </div>
        </div>
      </section>

      <section aria-labelledby="business-health-performance-heading">
        <div className="mb-1 flex items-center justify-between gap-4">
          <h3 id="business-health-performance-heading" className="text-lg font-bold text-gray-900">
            {t('healthScore.breakdown.performanceDrivers')}
          </h3>
          <span className="hidden text-[11px] font-bold uppercase tracking-[0.12em] text-gray-500 sm:block">
            {t('healthScore.breakdown.score')}
          </span>
        </div>

        <div className="divide-y divide-gray-200">
          {breakdownEntries.map(([metric, data]) => {
            const metricLabel = t(`healthScore.metrics.${metric}`)
            const metricScore = Math.max(0, Math.min(100, Number(data.score) || 0))
            const metricStatus = data.status || 'neutral'
            const statusText = t(`healthScore.statusBadge.${metricStatus}`, t(`healthScore.status.${metricStatus}`))

            return (
              <div
                key={metric}
                className="grid grid-cols-1 gap-3 py-4 sm:grid-cols-[minmax(155px,0.75fr)_minmax(0,2fr)_112px] sm:items-center sm:gap-5"
              >
                <div className="min-w-0">
                  <p className="text-base font-semibold leading-5 text-gray-800">{metricLabel}</p>
                  <p className="mt-1 text-xs leading-4 text-gray-500">
                    {t('healthScore.breakdown.weightLabel', { weight: data.weight })}
                  </p>
                </div>

                <div className="min-w-0">
                  {breakdownNotes[metric] && (
                    <p className="mb-2 text-sm leading-5 text-gray-600">{breakdownNotes[metric]}</p>
                  )}
                  <div
                    role="progressbar"
                    aria-label={t('healthScore.breakdown.metricScore', { metric: metricLabel })}
                    aria-valuemin="0"
                    aria-valuemax="100"
                    aria-valuenow={metricScore}
                    className="h-2.5 w-full overflow-hidden rounded-full bg-gray-200"
                  >
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${getProgressColor(metricStatus)}`}
                      style={{ width: `${metricScore}%` }}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3 sm:flex-col sm:items-end sm:gap-1">
                  <span className="text-base font-bold leading-5 text-gray-800">
                    {data.score}<span className="font-medium text-gray-500"> / 100</span>
                  </span>
                  <span className={`text-xs font-semibold ${STATUS_TEXT_STYLES[metricStatus] || STATUS_TEXT_STYLES.neutral}`}>
                    {statusText}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      </section>

      <section className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-gray-50 p-5 sm:flex-row sm:items-center sm:justify-between sm:gap-8 sm:p-6">
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.12em] text-gray-500">
            {t('healthScore.breakdown.scoringMethod')}
          </p>
          <p className="mt-2 text-sm leading-5 text-gray-600">{t('healthScore.formula')}</p>
        </div>
        <div className="shrink-0 border-t border-gray-300 pt-3 sm:border-l sm:border-t-0 sm:pl-6 sm:pt-0 sm:text-right">
          <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-gray-500">
            {t('healthScore.businessImpactAverage')}
          </p>
          <p className="mt-1 text-2xl font-bold leading-7 tracking-tight text-gray-800">{score} / 100</p>
        </div>
      </section>
    </div>
  )

  return (
    <>
      <section className="h-full rounded-[14px] border border-gray-200 bg-white p-5">
        <header className="flex items-center justify-between gap-3">
          <h2 className="text-[15px] font-bold text-gray-900">{t('healthScore.compactTitle')}</h2>
          <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${scoreBadgeClass}`}>
            {t(`healthScore.statusBadge.${status}`, t(`healthScore.status.${status}`))}
          </span>
        </header>

        <div className="mt-4 flex items-center gap-4">
          <div
            className="relative flex h-[86px] w-[86px] shrink-0 items-center justify-center rounded-full bg-gray-100"
            role="img"
            aria-label={t('healthScore.score', { score })}
          >
            <span className="text-2xl font-extrabold leading-none text-gray-900">{score}</span>
            <svg className="absolute inset-0 -rotate-90" width="86" height="86" viewBox="0 0 86 86" aria-hidden="true">
              <circle cx="43" cy="43" r="37" fill="none" stroke="#E8EAEE" strokeWidth="7" />
              <circle
                cx="43"
                cy="43"
                r="37"
                fill="none"
                stroke="#FF1B54"
                strokeWidth="7"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
              />
            </svg>
          </div>
          <div className="min-w-0">
            <p className="text-[22px] font-bold leading-7 text-gray-400">{t('healthScore.compactScore', { score })}</p>
            <p className={`text-[13px] font-semibold ${STATUS_TEXT_STYLES[status] || STATUS_TEXT_STYLES.neutral}`}>
              {formattedScore.description}
            </p>
            <p className="mt-1 text-xs text-gray-500">{t('healthScore.metricsBasis')}</p>
          </div>
        </div>

        <div className="mt-4 rounded-[10px] border border-[#FFD4DF] bg-[#FFF5F7] px-3 py-3">
          <h3 className="text-xs font-bold text-[#C91545]">{t('healthScore.recommendedNextStep')}</h3>
          <p className="mt-1 text-[13px] font-semibold leading-4 text-gray-900">
            {recommendation?.title || t('healthScore.recommendations.noRecommendations')}
          </p>
          {recommendation?.message && (
            <p className="mt-1 text-xs leading-[17px] text-gray-500">{recommendation.message}</p>
          )}
        </div>

        <div className="mt-4 flex items-center justify-between gap-3 text-sm">
          <span className="text-gray-500">{t('healthScore.gradeBands')}</span>
          <button
            type="button"
            aria-expanded={showGradeCriteria}
            aria-controls="business-grade-criteria"
            onClick={() => setShowGradeCriteria((open) => !open)}
            className="font-semibold text-[#E1174C] hover:text-[#C91545] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF1B54] focus-visible:ring-offset-2"
          >
            {showGradeCriteria ? t('healthScore.hideGradeBreakdown') : t('healthScore.showBreakdown')}
          </button>
        </div>

        {showGradeCriteria && (
          <div id="business-grade-criteria" className="mt-3 rounded-[10px] border border-gray-200 p-3">
            <h3 className="mb-2 text-sm font-semibold text-gray-700">{t('healthScore.businessGradeCriteria')}</h3>
            <div className="space-y-2 text-xs text-gray-600">
              {GRADE_CRITERIA.map((item) => {
                const isCurrentGrade = (status === 'excellent' ? 'A+' : status === 'good' ? 'B' : status === 'warning' ? 'C' : 'D') === item.grade
                return (
                  <div
                    key={item.grade}
                    className={`flex items-center justify-between gap-2 rounded-md px-2.5 py-1.5 ${
                      isCurrentGrade ? 'border border-emerald-200 bg-emerald-50' : 'bg-gray-50'
                    }`}
                  >
                    <span className="font-medium text-gray-800">
                      {item.grade} - {t(`healthScore.grades.${item.grade}`)}
                    </span>
                    <span className="shrink-0">{t('healthScore.scoreRange', { range: item.range })}</span>
                  </div>
                )
              })}
            </div>
            <button
              type="button"
              onClick={() => setShowBreakdown(true)}
              className="mt-3 w-full rounded-lg bg-[#FF1B54] px-3 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#E1174C] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF1B54] focus-visible:ring-offset-2"
            >
              {t('healthScore.viewBusinessScoreBreakdown')}
            </button>
          </div>
        )}
      </section>

      {showBreakdown && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3 sm:p-6">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="business-health-breakdown-title"
            className="flex max-h-[calc(100vh-1.5rem)] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl sm:max-h-[calc(100vh-3rem)] sm:rounded-[24px]"
          >
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-200 px-5 py-5 sm:px-8 sm:py-6">
              <div className="min-w-0">
                <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.14em] text-[#C91545]">
                  {t('healthScore.breakdown.eyebrow')}
                </p>
                <h2 id="business-health-breakdown-title" className="text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">
                  {t('healthScore.breakdownModalTitle')}
                </h2>
                <p className="mt-1 text-sm leading-5 text-gray-600">
                  {t('healthScore.breakdown.subtitle')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowBreakdown(false)}
                aria-label={t('customer.reportsClose')}
                title={t('customer.reportsClose')}
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-transparent bg-[#FF1B54] text-white transition-colors hover:bg-[#E1174C] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF1B54] focus-visible:ring-offset-2"
              >
                <X aria-hidden="true" className="h-5 w-5" strokeWidth={2} />
              </button>
            </div>
            <div className="min-h-0 overflow-y-auto px-5 py-5 sm:px-8 sm:py-6">{breakdownContent}</div>
          </div>
        </div>
      )}
    </>
  )
})

HealthScoreCard.displayName = 'HealthScoreCard'

export default HealthScoreCard
